import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import React from "react";
import { Landmark, Plus, Trash2, Globe } from "lucide-react";
import { isMerchantProfileUpdateAllowed, isUserProfileUpdateAllowed } from "../../authGuard";
import { PaymentMethodStatusType, PaymentMethodType, PaymentMethods } from "../../models/FinancialData";
import { AppBadge, StatusBadge } from "../../components/ui";
import { confirmDanger, confirmAction } from "../../utils/confirmHelpers";
import {
    CreateCardPaymentMethodRequest,
    CreateIbanPaymentMethodRequest,
    CreateNonIbanPaymentMethodRequest,
    PaymentMethodStatus,
} from "../../models/MerchantModel";
import { Dropdown } from "primereact/dropdown";
import { fileService } from "../../services/FileService";
import saveAs from "file-saver";
import JSZip from "jszip";
import { toastService } from "../../services/toastService";

interface PaymentMethodCrudComponentProps {
    addPaymentMethod: (type: "Bank" | "NonIBAN") => void;
    deletePaymentMethod: (item: PaymentMethods) => void;
    updatePaymentMethod: (item: CreateCardPaymentMethodRequest | CreateIbanPaymentMethodRequest | CreateNonIbanPaymentMethodRequest) => void;
    paymentMethods: PaymentMethods[];
}

interface PaymentMethodCrudComponentState {
    isLoading: boolean;
    fileBase64: string;
}

export class PaymentMethodCrudComponent extends React.Component<PaymentMethodCrudComponentProps, PaymentMethodCrudComponentState> {
    constructor(props: any | Readonly<any>) {
        super(props);

        this.state = {
            isLoading: false,
            fileBase64: "",
        };
    }
    changeCardStatusDialog = (e: any, rowData: any) => {
        confirmAction(
            "Are you sure to reject/approve this payment method?",
            "Confirmation",
            () => {
                this.updatePaymenMethodtStatus(e, rowData);
            },
        );
    };
    reject = () => {
        toastService.warn("Operation Cancelled", "Rejected");
    };
    addPaymentMenuItems = [
        {
            label: "Add Non-IBAN Account",
            icon: <Globe size={16} />,
            command: () => this.props.addPaymentMethod("NonIBAN"),
        },
    ];

    renderHeader = () => {
        return (
            <div className="flex justify-end">
                {(isUserProfileUpdateAllowed() || isMerchantProfileUpdateAllowed()) && (
                    <SplitButton
                        label="Add Bank Account"
                        icon={<Plus size={16} />}
                        model={this.addPaymentMenuItems}
                        onClick={() => this.props.addPaymentMethod("Bank")}
                    />
                )}
            </div>
        );
    };

    confirmDeletePaymentMethod = async (item: PaymentMethods) => {
        confirmDanger(
            "This action will remove the method and is irreversible. Are you sure you want to proceed?",
            "Delete Payment Method",
            () => this.props.deletePaymentMethod(item),
        );
    };

    actionBodyTemplate = (rowData: PaymentMethods) => {
        return (
            <button
                type="button"
                className="p-1.5 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                onClick={() => this.confirmDeletePaymentMethod(rowData)}
                title="Delete payment method"
            >
                <Trash2 size={15} className="text-neutral-400 hover:text-danger-500 transition-colors" />
            </button>
        );
    };

    renderStatus(rowData: PaymentMethods) {
        const statustype = PaymentMethodStatusType[rowData.status as keyof typeof PaymentMethodStatusType];
        if (rowData.status === PaymentMethodStatus.Approve) {
            return <AppBadge variant="success">{statustype}</AppBadge>;
        }
        if (rowData.status === PaymentMethodStatus.Reject) {
            return <AppBadge variant="danger">{statustype}</AppBadge>;
        }
        if (rowData.status === PaymentMethodStatus.UnderReview) {
            return (
                <Dropdown
                    optionLabel="name"
                    optionValue="id"
                    options={[
                        { name: "Approve", id: "1" },
                        { name: "Reject", id: "3" },
                    ]}
                    onChange={(e) => this.changeCardStatusDialog(e, rowData)}
                    placeholder={statustype}
                    className="w-full"
                />
            );
        }
    }

    updatePaymenMethodtStatus = async (e: any, rowData: PaymentMethods) => {
        this.setState({ isLoading: true });
        if (rowData.paymentType === PaymentMethodType.Card) {
            const cardObj: CreateCardPaymentMethodRequest = {
                cardAlias: rowData.alias,
                cardHolderName: rowData.ownerName,
                cardNumber: rowData.value,
                cardType: rowData.cardType,
                cvvOrCvcCode: rowData.cvvOrCvcCode,
                validityMonth: rowData.validityMonth,
                validityYear: rowData.validityYear,
                Currency: "BGN",
                status: e.target.value,
                paymentType: rowData.paymentType,
            };
            this.props.updatePaymentMethod(cardObj);
        }

        if (rowData.paymentType === PaymentMethodType.Bank) {
            var imagePath = await fileService.GetFile(rowData.bankStatement); // base64
            const IbanOrBankObj: CreateIbanPaymentMethodRequest = {
                bankName: rowData.bankName,
                accountName: rowData.ownerName,
                bankStatementFileName: rowData.bankStatement,
                paymentType: rowData.paymentType,
                ibanNumber: rowData.value,
                swift: rowData.swift,
                status: e.target.value,
                bankStatement: imagePath.data,
                currency: rowData.currency,
            };
            this.props.updatePaymentMethod(IbanOrBankObj);
        }

        if (rowData.paymentType === PaymentMethodType.NonIBAN) {
            var imagePath = await fileService.GetFile(rowData.bankStatement); // base64
            const nonIBANObj: CreateNonIbanPaymentMethodRequest = {
                bankName: rowData.bankName,
                accountHolderName: rowData.accountHolderName,
                bankAccountName: rowData.ownerName,
                bankAddress: rowData.bankAddress ?? "none",
                bankStatementFileName: rowData.bankStatement,
                paymentType: rowData.paymentType,
                bankAccountNumber: rowData.value,
                swift: rowData.swift,
                status: e.target.value,
                bankStatement: imagePath.data,
                currency: rowData.currency,
            };
            this.props.updatePaymentMethod(nonIBANObj);
        }
        this.setState({ isLoading: false });
    };

    onDownloadFromFileStorage = async (fileStorageId: string, secondaryLabel: string, photoZip: any): Promise<void> => {
        const imagePath = await fileService.GetFile(fileStorageId); // base64
        if (!imagePath.success) {
            //this.toast?.show({ severity: "error", summary: "Error", detail: imagePath.message, life: 3000 });
            return;
        }
        photoZip.file(secondaryLabel, imagePath.data, { base64: true });
    };

    downloadDocuments = async (rowData: PaymentMethods) => {
        this.setState({ isLoading: true });
        if (rowData.bankStatement != "") {
            const fileExtenion = rowData.bankStatement.split(".")[1];
            if (fileExtenion) {
                const zip = new JSZip();
                const photoZip = zip.folder("documents");
                await this.onDownloadFromFileStorage(rowData.bankStatement, `${"document." + fileExtenion}`, photoZip);
                /// waits for "await DownloadFileFromS3, then executes the save as which saves the zipped folder created in "DownloadFileFromS3"
                const content = await zip.generateAsync({ type: "blob" });
                saveAs(content, "documents");
            }
        }
        this.setState({ isLoading: false });
    };

    renderDownload(rowData: PaymentMethods) {
        if (rowData.bankStatement != "") {
            return (
                <Button label="Download" size="small" outlined onClick={() => this.downloadDocuments(rowData)} />
            );
        }
    }

    render() {
        return (
            <div>
                <DataTable header={this.renderHeader} loading={this.state.isLoading} value={this.props.paymentMethods}>
                    <Column field="paymentType" header="Type" />
                    <Column
                        header="Pan / IBAN"
                        body={(rowData: PaymentMethods) =>
                            rowData.paymentType === 'Card' ? rowData.alias : rowData.value
                        }
                    />

                    <Column field="ownerName" header="Owner Names On" />
                    <Column field="lastFundsDeposited" header="Last Funds Deposited" />
                    <Column field="lastFundsWithdrawn" header="Last Funds Withdrawn" />
                    <Column header="Status" body={(rowData: PaymentMethods) => this.renderStatus(rowData)} />

                    <Column body={(rowData: PaymentMethods) => this.renderDownload(rowData)} header="Uploaded Files" />
                    <Column
                        field="has2FAOr3DS"
                        header="2FA / 3DS"
                        body={(rowData: PaymentMethods) => StatusBadge(rowData.has2FAOr3DS, "Yes", "No")}
                    />
                    <Column header="" body={this.actionBodyTemplate} headerStyle={{ width: "8em", textAlign: "center" }} />
                </DataTable>
            </div>
        );
    }
}
